import {
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  GridOptions,
} from 'ag-grid-community'
import { BulkSheetView } from '../../../../containers/BulkSheetView'
import Loading from '../../../../components/process-state-notifications/Loading'
import { useCallback, useEffect, useRef } from 'react'
import CancelConfirmDialog from '../../../../components/dialogs/CancelConfirmDialog'
import {
  TaskActualWorkTaskDialogState,
  openTaskActualWorkListProps,
} from '../../hooks/taskActualWorkTaskDialogState'
import TaskActualWorkTaskDialog from '../../../../components/dialogs/TaskActualWorkTaskDialog'
import DateVO from '../../../../../vo/DateVO'
import {
  CustomEnumCode,
  getCustomEnumValues,
} from '../../../../../lib/functions/customEnumValue'
import { parse } from '../../../../../lib/commons/i18nLabel'
import {
  refreshDynamicColumnDef,
  updateDisplayProjectColumn,
} from '../../gridOptions'
import { useAssignedProjectsData } from '../../../ActualWorkingHours/hooks/assignedProjectsData'
import {
  ActualWorkingHoursRatioRow,
  ActualWorkingHoursRow,
  ActualWorkingHoursSummaryRow,
} from '../../../ActualWorkingHours/ActualWorkingHours'
import { featureFlagService } from '../../../../../utils/feature-flag'

interface Props {
  gridOptions: GridOptions
  loading: boolean
  userUuid: string | undefined
  projectUuid?: string | undefined
  data: ActualWorkingHoursRow[]
  summaryRow: ActualWorkingHoursSummaryRow
  ratioRow: ActualWorkingHoursRatioRow
  onCallbackReload?: () => void
  taskDialogState: TaskActualWorkTaskDialogState | null
  setTaskDialogState: (state: TaskActualWorkTaskDialogState | null) => void
  openTaskActualWorkList: (props: openTaskActualWorkListProps) => Promise<void>
  openCancel?: boolean
  setOpenCancel?: (value: boolean) => void
  rowHeight: number
  gridApi?: GridApi
  updateContext: (partial: Record<string, any>) => void
  onGridReady?: (e: GridReadyEvent) => void
}

const ActualWorkingHoursView = (props: Props) => {
  const {
    gridOptions,
    loading,
    userUuid,
    projectUuid,
    data,
    summaryRow,
    ratioRow,
    onCallbackReload,
    taskDialogState,
    setTaskDialogState,
    openTaskActualWorkList,
    openCancel,
    setOpenCancel,
    rowHeight,
    gridApi,
    updateContext: parentUpdateContext,
    onGridReady: parentOnGridReady,
  } = props
  const { assignedProjects } = useAssignedProjectsData({
    userUuid,
    projectUuid,
  })
  const ref = useRef<HTMLDivElement>(null)
  const contextMenu = useCallback((params: GetContextMenuItemsParams) => {
    return []
  }, [])

  const fetchSelectColumnOptions = useCallback(async () => {
    const response = await getCustomEnumValues({
      customEnumCode: CustomEnumCode.WORKING_HOURS_TYPE,
    })
    const workingHoursType = response.json.map(v => ({
      ...v,
      nameI18n: parse(v.nameI18n),
    }))
    parentUpdateContext({ workingHoursType })
  }, [parentUpdateContext])

  const onCancelConfirm = useCallback(() => {
    setOpenCancel && setOpenCancel(false)
    onCallbackReload && onCallbackReload()
  }, [onCallbackReload, setOpenCancel])

  useEffect(() => {
    fetchSelectColumnOptions()
  }, [fetchSelectColumnOptions])

  useEffect(() => {
    refreshDynamicColumnDef({
      api: gridApi,
      projects: assignedProjects,
      userUuid,
      openTaskActualWorkList,
      data,
    })
  }, [gridApi, assignedProjects, userUuid, openTaskActualWorkList, data])

  useEffect(() => {
    updateDisplayProjectColumn(gridApi, data)
  }, [gridApi, data])

  return (
    <>
      <BulkSheetView
        ref={ref}
        gridOptions={gridOptions}
        rowHeight={rowHeight}
        rowData={data}
        pinnedTopRowData={
          featureFlagService.isEnabled('task-actual-work-application')
            ? [ratioRow, summaryRow]
            : [summaryRow]
        }
        getContextMenuItems={contextMenu}
        onGridReady={parentOnGridReady}
      />
      <Loading isLoading={loading} elem={ref.current} />
      <CancelConfirmDialog
        open={openCancel ?? false}
        onConfirm={onCancelConfirm}
        onClose={() => setOpenCancel && setOpenCancel(false)}
      />
      {taskDialogState && (
        <TaskActualWorkTaskDialog
          anchorEl={taskDialogState.anchorElement}
          date={new DateVO(taskDialogState.date)}
          actualWorks={taskDialogState.taskActualWorks}
          actualHour={taskDialogState.total}
          event={taskDialogState.clickEvent as PointerEvent}
          onClose={() => setTaskDialogState(null)}
          openDetail={taskDialogState.openDetail}
        />
      )}
    </>
  )
}

export default ActualWorkingHoursView
